import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-multi-date-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import {
    CakeIcon,
    CalendarIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    EyeIcon,
    FileDownIcon,
    FileSpreadsheetIcon,
    InfoIcon,
    LinkIcon,
    ListFilterIcon,
    PackageIcon,
    PencilIcon,
    SearchIcon,
    UploadIcon,
    XCircleIcon,
    XIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import gregorian_pt from '@/services/gregorian_pt';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import { useSearchTextInput } from '@/hooks/useSearchTextInput';
import server from '@/services/server';
import {
    SUBSCRIPTION_STATUS_OPTIONS,
    getSubscriptionStatusInfo,
} from '../utils/subscriptionStatus';
import DialogSubscriber from '../components/DialogSubscriber';
import ClubEmptyList from '../components/ClubEmptyList';
import SubscriptionDetailsModal from '../components/SubscriptionDetailsModal';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import AsaasLinkDialog from '../components/AsaasLinkDialog';
import { useAuth } from '@/context/AuthContext';
import { dateRangeLabel, patchSearchParams, serializeDateRange } from '../utils/urlListFilters';

const ORDER_OPTIONS = {
    RECENT: { value: 'recent', label: 'Mais recentes', orderBy: { createdAt: 'desc' } },
    OLDEST: { value: 'oldest', label: 'Mais antigos', orderBy: { createdAt: 'asc' } },
    NAME_ASC: { value: 'name_asc', label: 'Nome (A-Z)', orderBy: { name: 'asc' } },
    NAME_DESC: { value: 'name_desc', label: 'Nome (Z-A)', orderBy: { name: 'desc' } },
    ID_DESC: { value: 'id_desc', label: 'ID (maior)', orderBy: { id: 'desc' } },
    ID_ASC: { value: 'id_asc', label: 'ID (menor)', orderBy: { id: 'asc' } },
};

const ALL_VALUE = 'all';

const SUBSCRIBER_STATUS_ITEMS = {
    [ALL_VALUE]: 'Todos',
    true: 'Ativo',
    false: 'Inativo',
};

const BENEFIT_TYPE_ITEMS = {
    [ALL_VALUE]: 'Todos',
    telemedicina: 'Telemedicina',
    telepsicologia: 'Telepsicologia',
    seguro: 'Seguro',
    telepet: 'Telepet',
};

const ORDER_ITEMS = Object.fromEntries(Object.values(ORDER_OPTIONS).map(option => [option.value, option.label]));

const SUBSCRIPTION_STATUS_ITEMS = {
    [ALL_VALUE]: 'Todos',
    ...Object.fromEntries(SUBSCRIPTION_STATUS_OPTIONS.map(option => [option.value, option.label])),
};

const BILLING_TYPE_ITEMS = {
    [ALL_VALUE]: 'Todas',
    MONEY: 'Dinheiro',
    BOLETO: 'Boleto',
    PIX: 'PIX',
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
};

const BILLING_MODEL_ITEMS = {
    [ALL_VALUE]: 'Todos',
    RECURRING: 'Recorrente / dinheiro',
    INSTALLMENT: 'Anual parcelado',
};

function installmentLabel(subscription) {
    const isInstallment =
        String(subscription?.billingModel || '').toUpperCase() === 'INSTALLMENT' ||
        Boolean(subscription?.installmentExternalId);
    if (!isInstallment) return null;
    const count = subscription?.installments?.[0]?.installmentCount;
    return count ? `Parcelado ${count}x` : 'Parcelado';
}

function isClickInsideFilterPortal(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(
        target.closest('[data-slot="select-content"]') ||
            target.closest('[data-slot="select-positioner"]') ||
            target.closest('.rmdp-wrapper') ||
            target.closest('.rmdp-calendar') ||
            target.closest('.rmdp-container') ||
            target.closest('[class*="rmdp-"]'),
    );
}

function formatBirthday(date) {
    const raw = date;
    const [y, m, d] = raw.split('T')[0].split('-');
    const formatted = `${d}/${m}/${y}`;
    return formatted;
}

function formatCurrency(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function FilterField({ label, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{label}</label>
            {children}
        </div>
    );
}

export default function ListSubscribers({ clubId }) {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const { user } = useAuth();
    const [asaasLinkOpen, setAsaasLinkOpen] = useState(false);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [detailsSubscription, setDetailsSubscription] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const filtersPanelRef = useRef(null);
    const exportMenuRef = useRef(null);

    const { club } = useClub();
    const { handleOpen: openSubscriberDialog } = useDialog('#assinantes');
    const clubData = club || {};
    const hasReseller = club?.functions?.includes('reseller');
    const hasBenefits = club?.functions?.includes('benefit');
    const showPlanUi = hasReseller || hasBenefits;
    const showAsaasScan = Boolean(user?.super) && Boolean(club?.hasAsaasKey);
    const { data: plans } = useFetch('club/settings/plans');

    const planItems = React.useMemo(() => {
        const items = { [ALL_VALUE]: 'Todos' };
        if (Array.isArray(plans)) {
            plans.forEach(plan => {
                items[String(plan.id)] = plan.name;
            });
        }
        return items;
    }, [plans]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedSubscription(null);
    };

    const handleViewDetails = subscriber => {
        setDetailsSubscription(subscriber || null);
        setOpenDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setOpenDetailsModal(false);
        setDetailsSubscription(null);
    };

    function statusSubscriberLabel(status) {
        if (status === true) return 'Ativo';
        if (status === false) return 'Inativo';
        return '—';
    }

    function statusbenefits(status) {
        if (status === 'ACTIVE') return 'Ativo';
        if (status === 'CANCELLED') return 'Inativo';
        if (status === 'EXPIRED') return 'Expirado';
        if (status === 'PENDING') return 'Pendente';

        return '—';
    }

    const page = parseInt(searchParams.get('page'), 10) || 1;
    const qFromUrl = searchParams.get('q') || '';
    const birthday = serializeDateRange(searchParams.get('birthday') || '');
    const subscriberStatus = searchParams.get('status') || '';
    const statusBenefits = searchParams.get('statusBenefits') || '';
    const benefitType = searchParams.get('benefitType') || '';
    const subscriptionStatus = searchParams.get('subscriptionStatus') || '';
    const planId = searchParams.get('planId') || '';
    const billingType = searchParams.get('billingType') || '';
    const billingModel = searchParams.get('billingModel') || '';
    const orderBy = searchParams.get('order') || 'recent';

    const { search, input, handleChange, sync } = useSearchTextInput(qFromUrl, 500);

    useEffect(() => {
        if (qFromUrl !== search) sync(qFromUrl);
    }, [qFromUrl]);

    useEffect(() => {
        if (search === qFromUrl) return;
        patchSearchParams(setSearchParams, { q: search });
    }, [search, qFromUrl, setSearchParams]);

    const setFilter = (key, value) => {
        patchSearchParams(setSearchParams, { [key]: value });
    };

    const filters = React.useMemo(() => {
        let f = { clubId: id };
        if (search) f.search = search;
        if (birthday.length) f.birthday = birthday;
        if (subscriberStatus !== '') f.status = subscriberStatus;
        if (statusBenefits !== '') f.statusBenefits = statusBenefits;
        if (benefitType !== '') f.benefitType = benefitType;
        if (subscriptionStatus !== '') f.subscriptionStatus = subscriptionStatus;
        if (planId !== '') f.planId = Number(planId);
        if (billingType !== '') f.billingType = billingType;
        if (billingModel !== '') f.billingModel = billingModel;

        const selectedOrder = Object.values(ORDER_OPTIONS).find(opt => opt.value === orderBy);
        if (selectedOrder) {
            f.orderBy = selectedOrder.orderBy;
        }

        f.page = page || 1;
        f.limit = 10;
        return f;
    }, [id, search, birthday, subscriberStatus, statusBenefits, benefitType, subscriptionStatus, planId, billingType, billingModel, orderBy, page]);

    const exportFilters = React.useMemo(() => {
        const { page: _page, limit: _limit, ...rest } = filters;
        return rest;
    }, [filters]);

    const activeFiltersCount = React.useMemo(() => {
        let count = 0;
        if (birthday) count++;
        if (subscriberStatus !== '') count++;
        if (statusBenefits !== '') count++;
        if (benefitType !== '') count++;
        if (subscriptionStatus !== '') count++;
        if (showPlanUi && planId !== '') count++;
        if (billingType !== '') count++;
        if (billingModel !== '') count++;
        if (orderBy !== 'recent') count++;
        return count;
    }, [birthday, subscriberStatus, statusBenefits, benefitType, subscriptionStatus, planId, billingType, billingModel, orderBy, showPlanUi]);

    const clearFilters = () => {
        patchSearchParams(setSearchParams, {
            birthday: '',
            status: '',
            statusBenefits: '',
            benefitType: '',
            subscriptionStatus: '',
            planId: '',
            billingType: '',
            billingModel: '',
            order: '',
        });
    };

    useEffect(() => {
        if (!filtersOpen) return;

        function handlePointerDown(event) {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (filtersPanelRef.current?.contains(target)) return;
            if (isClickInsideFilterPortal(target)) return;
            setFiltersOpen(false);
        }

        function handleKeyDown(event) {
            if (event.key === 'Escape') setFiltersOpen(false);
        }

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [filtersOpen]);

    useEffect(() => {
        if (!exportMenuOpen) return;

        function handlePointerDown(event) {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (exportMenuRef.current?.contains(target)) return;
            setExportMenuOpen(false);
        }

        function handleKeyDown(event) {
            if (event.key === 'Escape') setExportMenuOpen(false);
        }

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [exportMenuOpen]);

    const { data, mutate, isFetching } = useFetch('club/subscribers', {
        params: filters,
    });

    function planVerify(subscriber) {
        const plan = subscriber?.subscription?.Plan;
        const planName = plan?.name;
        const isYearly = String(subscriber?.subscription?.cycle || '').toUpperCase() === 'YEARLY';
        const pricePlan = Number(
            isYearly ? subscriber?.subscription?.value ?? plan?.yearlyPrice : plan?.price,
        );

        if (!planName) {
            return 'Sem plano definido';
        }

        // Clube sem revenda: plano não é comercializado — só o nome, sem valor.
        if (!hasReseller) {
            return planName;
        }

        if (pricePlan === 0) {
            return 'Plano gratuito';
        }

        return `${planName} - ${formatCurrency(pricePlan)}/${isYearly ? 'ano' : 'mês'}`;
    }

    const handleExportSubscribers = async () => {
        setExportLoading(true);
        try {
            const { data: body } = await server.get('club/subscribers/export', { params: exportFilters });

            const rows = Array.isArray(body?.data) ? body.data : [];
            const sheetRows = rows.map(row => ({
                Id: row.Id ?? '',
                Nome: row.Nome ?? '',
                Tipo: row.Tipo ?? '',
                CPF: row.CPF ?? '',
                Email: row.Email ?? '',
                'CPF do titular': row.CPF_Titular ?? '',
            }));
            sheetRows.push({
                Id: '',
                Nome: 'Totais',
                Tipo: '',
                CPF: '',
                Email: `${rows.length} cadastrado(s)`,
                'CPF do titular': '',
            });

            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Assinantes');
            const stamp = format(new Date(), 'yyyy-MM-dd-HHmm', { locale: ptBR });
            XLSX.writeFile(wb, `cadastrados-${clubData?.id ?? 'clube'}-${stamp}.xlsx`);

            enqueueSnackbar(
                rows.length ? `Planilha gerada com ${rows.length} cadastrado(s).` : 'Nenhum cadastrado para exportar.',
                { variant: rows.length ? 'success' : 'error' },
            );
        } catch (error) {
            console.log('error', error);
            setErrors(error);
            enqueueSnackbar('Erro ao exportar assinantes.', { variant: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportSubscribersPdf = async () => {
        setExportLoading(true);
        try {
            const response = await server.get('club/subscribers/export', {
                params: { ...exportFilters, format: 'pdf' },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const stamp = format(new Date(), 'yyyy-MM-dd-HHmm', { locale: ptBR });
            link.href = url;
            link.setAttribute('download', `cadastrados-${clubData?.id ?? 'clube'}-${stamp}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('PDF gerado com sucesso.', { variant: 'success' });
        } catch (error) {
            setErrors(error);
            enqueueSnackbar('Erro ao exportar PDF.', { variant: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const PickerTemplate = ({ openCalendar, value, setValue }) => (
        <div className="relative w-full">
            <Input
                readOnly
                placeholder="Selecione um período"
                value={dateRangeLabel(value)}
                className="pl-9 pr-9"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-1">
                <Button type="button" variant="ghost" size="icon-sm" onClick={openCalendar} aria-label="Abrir calendário">
                    <CalendarIcon className="size-4 text-muted-foreground" />
                </Button>
            </div>
            {value ? (
                <div className="absolute inset-y-0 right-0 flex items-center pr-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setValue('')}
                        aria-label="Limpar data"
                    >
                        <XIcon className="size-4 text-muted-foreground" />
                    </Button>
                </div>
            ) : null}
        </div>
    );

    const rows = data?.rows || [];
    const totalPages = data?.pages || data?.totalPages || 1;

    return (
        <div className="flex flex-col gap-6">
            <Card className="overflow-visible">
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
                    <div>
                        <CardTitle>Assinantes</CardTitle>
                        <CardDescription>Adicione e gerencie seus assinantes.</CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {showAsaasScan ? (
                            <Button type="button" variant="outline" size="sm" onClick={() => setAsaasLinkOpen(true)}>
                                <LinkIcon className="size-4" />
                                Vincular Asaas
                            </Button>
                        ) : null}
                        <Button variant="outline" size="sm" render={<Link to="importacoes" />}>
                            <UploadIcon className="size-4" />
                            Importações
                        </Button>
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                disabled={exportLoading}
                                aria-expanded={exportMenuOpen}
                                aria-haspopup="menu"
                                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
                                onClick={() => setExportMenuOpen(open => !open)}
                            >
                                {exportLoading ? (
                                    <Spinner className="size-4" />
                                ) : (
                                    <FileDownIcon className="size-4" />
                                )}
                                {exportLoading ? 'Exportando…' : 'Exportar'}
                                <ChevronDownIcon className="size-4 opacity-60" />
                            </button>

                            {exportMenuOpen ? (
                                <div
                                    role="menu"
                                    className="absolute right-0 z-50 mt-1 min-w-44 rounded-lg border bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
                                >
                                    <button
                                        type="button"
                                        role="menuitem"
                                        disabled={exportLoading}
                                        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            handleExportSubscribers();
                                        }}
                                    >
                                        <FileSpreadsheetIcon className="size-4" />
                                        Planilha Excel
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        disabled={exportLoading}
                                        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            handleExportSubscribersPdf();
                                        }}
                                    >
                                        <FileDownIcon className="size-4" />
                                        PDF
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        <Button type="button" size="sm" onClick={() => openSubscriberDialog()}>
                            Novo
                        </Button>
                    </div>
                </CardHeader>
            </Card>

            <Alert variant="info">
                <InfoIcon />
                <AlertTitle>Senha de acesso</AlertTitle>
                <AlertDescription>
                    A senha provisória do assinante são os <strong>6 primeiros dígitos do CPF/CNPJ</strong>. No
                    cadastro pelo painel, importação ou API, o assinante recebe um e-mail de boas-vindas com esses
                    dados.
                </AlertDescription>
            </Alert>

            <Card className="overflow-visible">
                <div className="flex flex-wrap items-center gap-2 border-b p-4">
                    <div className="relative min-w-[220px] flex-1">
                        <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Buscar por ID, nome, documento ou email..."
                            value={input}
                            onChange={e => handleChange(e.target.value)}
                            className="pl-8"
                        />
                    </div>

                    <div className="relative" ref={filtersPanelRef}>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="relative"
                            aria-expanded={filtersOpen}
                            onClick={() => setFiltersOpen(open => !open)}
                        >
                            <ListFilterIcon className="size-4" />
                            Filtros
                            {activeFiltersCount > 0 ? (
                                <Badge variant="default" className="ml-1 size-5 justify-center rounded-full px-0 text-[10px]">
                                    {activeFiltersCount}
                                </Badge>
                            ) : null}
                        </Button>

                        {filtersOpen ? (
                            <div className="absolute top-[calc(100%+0.5rem)] right-0 z-50 flex w-80 max-h-[min(70vh,480px)] flex-col overflow-hidden rounded-lg bg-popover text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10">
                                <div className="shrink-0 border-b px-4 py-3 font-medium">Filtros</div>
                                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
                                    <div className="flex flex-col gap-4">
                                <FilterField label="Data de aniversário">
                                    <DatePicker
                                        containerClassName="w-full"
                                        onChange={e => setFilter('birthday', serializeDateRange(e).join(','))}
                                        format="DD/MM/YYYY"
                                        range
                                        dateSeparator=" a "
                                        locale={gregorian_pt}
                                        value={birthday}
                                        icon={<CakeIcon className="size-4 text-muted-foreground" />}
                                        render={
                                            <PickerTemplate
                                                value={birthday}
                                                setValue={() => setFilter('birthday', '')}
                                            />
                                        }
                                    />
                                </FilterField>

                                <FilterField label="Status do assinante">
                                    <Select
                                        items={SUBSCRIBER_STATUS_ITEMS}
                                        value={subscriberStatus || ALL_VALUE}
                                        onValueChange={value => setFilter('status', value === ALL_VALUE ? '' : value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL_VALUE}>Todos</SelectItem>
                                            <SelectItem value="true">Ativo</SelectItem>
                                            <SelectItem value="false">Inativo</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FilterField>

                                {hasBenefits ? (
                                    <FilterField label="Status do benefício">
                                        <Select
                                            items={SUBSCRIBER_STATUS_ITEMS}
                                            value={statusBenefits || ALL_VALUE}
                                            onValueChange={value => setFilter('statusBenefits', value === ALL_VALUE ? '' : value)}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={ALL_VALUE}>Todos</SelectItem>
                                                <SelectItem value="true">Ativo</SelectItem>
                                                <SelectItem value="false">Inativo</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FilterField>
                                ) : null}

                                {hasBenefits ? (
                                    <FilterField label="Tipo de benefício">
                                        <Select
                                            items={BENEFIT_TYPE_ITEMS}
                                            value={benefitType || ALL_VALUE}
                                            onValueChange={value => setFilter('benefitType', value === ALL_VALUE ? '' : value)}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={ALL_VALUE}>Todos</SelectItem>
                                                <SelectItem value="telemedicina">Telemedicina</SelectItem>
                                                <SelectItem value="telepsicologia">Telepsicologia</SelectItem>
                                                <SelectItem value="seguro">Seguro</SelectItem>
                                                <SelectItem value="telepet">Telepet</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FilterField>
                                ) : null}

                                <FilterField label="Status da assinatura">
                                    <Select
                                        items={SUBSCRIPTION_STATUS_ITEMS}
                                        value={subscriptionStatus || ALL_VALUE}
                                        onValueChange={value => setFilter('subscriptionStatus', value === ALL_VALUE ? '' : value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL_VALUE}>Todos</SelectItem>
                                            {SUBSCRIPTION_STATUS_OPTIONS.map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FilterField>

                                <FilterField label="Forma de pagamento">
                                    <Select
                                        items={BILLING_TYPE_ITEMS}
                                        value={billingType || ALL_VALUE}
                                        onValueChange={value => setFilter('billingType', value === ALL_VALUE ? '' : value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL_VALUE}>Todas</SelectItem>
                                            <SelectItem value="MONEY">Dinheiro</SelectItem>
                                            <SelectItem value="BOLETO">Boleto</SelectItem>
                                            <SelectItem value="PIX">PIX</SelectItem>
                                            <SelectItem value="CREDIT_CARD">Cartão de crédito</SelectItem>
                                            <SelectItem value="DEBIT_CARD">Cartão de débito</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FilterField>

                                {hasReseller ? (
                                    <FilterField label="Modelo de cobrança">
                                        <Select
                                            items={BILLING_MODEL_ITEMS}
                                            value={billingModel || ALL_VALUE}
                                            onValueChange={value => setFilter('billingModel', value === ALL_VALUE ? '' : value)}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(BILLING_MODEL_ITEMS).map(([value, label]) => (
                                                    <SelectItem key={value} value={value}>
                                                        {label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FilterField>
                                ) : null}

                                {showPlanUi ? (
                                <FilterField label="Plano da assinatura">
                                    <Select
                                        items={planItems}
                                        value={planId || ALL_VALUE}
                                        onValueChange={value => setFilter('planId', value === ALL_VALUE ? '' : value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL_VALUE}>Todos</SelectItem>
                                            {Array.isArray(plans) &&
                                                plans.map(p => (
                                                    <SelectItem key={p.id} value={String(p.id)}>
                                                        {p.name}
                                                    </SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </FilterField>
                                ) : null}

                                <FilterField label="Ordenar por">
                                    <Select items={ORDER_ITEMS} value={orderBy} onValueChange={value => setFilter('order', value === 'recent' ? '' : value)}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.values(ORDER_OPTIONS).map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FilterField>
                                    </div>
                                </div>
                                {activeFiltersCount > 0 ? (
                                    <div className="shrink-0 border-t p-3">
                                        <Button type="button" variant="ghost" size="sm" className="w-full" onClick={clearFilters}>
                                            Limpar filtros
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>

                <CardContent className="p-0">
                    {isFetching && rows.length === 0 ? (
                        <div className="flex items-center justify-center gap-2 py-12">
                            <Spinner className="size-5" />
                            <span className="text-sm text-muted-foreground">Carregando...</span>
                        </div>
                    ) : null}

                    {!isFetching && rows.length === 0 ? (
                        <ClubEmptyList title="Nenhum assinante encontrado" />
                    ) : null}

                    {rows.length > 0 ? (
                        <div className="divide-y">
                            {rows.map(subscriber => {
                                const subscriptionStatusInfo = getSubscriptionStatusInfo(subscriber?.subscription);

                                return (
                                    <div key={subscriber?.id} className="flex items-start gap-4 p-4">
                                        <div
                                            className={cn(
                                                'flex size-14 shrink-0 items-center justify-center rounded-lg',
                                                subscriber?.status
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'bg-muted text-muted-foreground',
                                            )}
                                        >
                                            {subscriber?.status ? (
                                                <CheckCircle2Icon className="size-8" />
                                            ) : (
                                                <XCircleIcon className="size-8 text-destructive" />
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1 space-y-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-semibold">
                                                    {`${subscriber?.id} | ${subscriber?.name || 'N/A'}`}
                                                </p>
                                                {subscriber?.birthday ? (
                                                    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                                                        <CakeIcon className="size-4" />
                                                        {formatBirthday(subscriber.birthday)}
                                                    </span>
                                                ) : null}
                                            </div>

                                            {subscriber?.subscription ? (
                                                <div className="space-y-2">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        {showPlanUi ? (
                                                        <span className="text-sm font-medium text-muted-foreground">
                                                            {planVerify(subscriber) || 'N/A'}
                                                        </span>
                                                        ) : null}

                                                        {!Boolean(subscriber?.dependentBy) ? (
                                                            <>
                                                                <Badge
                                                                    variant={subscriptionStatusInfo.badgeVariant}
                                                                    className="text-xs"
                                                                >
                                                                    {subscriptionStatusInfo.label}
                                                                </Badge>
                                                                {hasReseller && installmentLabel(subscriber?.subscription) ? (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {installmentLabel(subscriber.subscription)}
                                                                    </Badge>
                                                                ) : null}
                                                                {Number(subscriber?._count?.Dependent) > 0 ? (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {subscriber._count.Dependent}{' '}
                                                                        {subscriber._count.Dependent === 1
                                                                            ? 'dependente'
                                                                            : 'dependentes'}
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-xs text-muted-foreground">
                                                                        Sem dependentes
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-sm text-muted-foreground">
                                                                {'Dependente de: ' + subscriber?.DependentBy?.name || 'N/A'}
                                                            </span>
                                                        )}

                                                        {subscriber?.subscription?.createdAt ? (
                                                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                                                <CalendarIcon className="size-3.5" />
                                                                {`Assinatura criada em: ${new Date(
                                                                    subscriber.subscription.createdAt,
                                                                ).toLocaleDateString('pt-BR')}`}
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    {subscriber?.subscription?.benefits?.length > 0 ? (
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            <PackageIcon className="size-4 text-muted-foreground" />
                                                            <span className="text-xs font-medium text-muted-foreground">
                                                                Produtos:
                                                            </span>
                                                            {subscriber.subscription.benefits.map(benefit => (
                                                                <Badge
                                                                    key={benefit.id}
                                                                    variant="outline"
                                                                    className="text-[0.7rem]"
                                                                >
                                                                    {benefit.aditionalName || 'Produto'}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="flex shrink-0 flex-wrap justify-end gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleViewDetails(subscriber)}
                                            >
                                                <EyeIcon className="size-4" />
                                                Assinatura
                                            </Button>

                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openSubscriberDialog(subscriber.id)}
                                            >
                                                <PencilIcon className="size-4" />
                                                Editar
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    {totalPages > 1 ? (
                        <div className="flex items-center justify-center gap-3 border-t px-4 py-4">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const params = new URLSearchParams(prev);
                                        params.set('page', String(page - 1));
                                        return params;
                                    })
                                }
                            >
                                Anterior
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Página {page} de {totalPages}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const params = new URLSearchParams(prev);
                                        params.set('page', String(page + 1));
                                        return params;
                                    })
                                }
                            >
                                Próxima
                            </Button>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <DialogSubscriber mutate={mutate} clubId={clubId} data={data} />
            <SubscriptionDetailsModal
                club={data?.Club}
                open={openDetailsModal}
                onClose={handleCloseDetailsModal}
                subscriber={detailsSubscription}
                onChanged={mutate}
                data={data}
            />
            {showAsaasScan ? (
                <AsaasLinkDialog open={asaasLinkOpen} onOpenChange={setAsaasLinkOpen} clubId={club?.id} />
            ) : null}
        </div>
    );
}
